// This threshold only offers a review. It never finishes or saves a workout.
export const WORKOUT_REMINDER_IDLE_MS = 4 * 60 * 60 * 1000

export function workoutDurationFields(durationSeconds, reviewRequired = false) {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0 || durationSeconds > 86400 || (reviewRequired && durationSeconds < 60)) {
    return { minutes: "", seconds: "" }
  }
  const total = Math.floor(durationSeconds)
  return { minutes: Math.floor(total / 60), seconds: total % 60 }
}

export function workoutDurationFromFields(minutes, seconds) {
  const minuteText = String(minutes ?? "").trim()
  const secondText = String(seconds ?? "").trim()
  if ((!minuteText && !secondText) || (minuteText && !/^\d+$/.test(minuteText)) || (secondText && !/^\d+$/.test(secondText))) return null
  const m = Number(minuteText), s = Number(secondText)
  const total = m * 60 + s
  return Number.isSafeInteger(total) && m <= 1440 && s < 60 && total > 0 && total <= 86400 ? total : null
}

export function formatWorkoutDuration(durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds < 0) return "Dauer offen"
  const total = Math.floor(durationSeconds)
  const hours = Math.floor(total / 3600), minutes = Math.floor((total % 3600) / 60), seconds = total % 60
  return [hours ? `${hours} Std.` : "", minutes ? `${minutes} Min.` : "", seconds || !total ? `${seconds} Sek.` : ""].filter(Boolean).join(" ")
}

export function workoutTimestamp(value) {
  if (value === null || value === undefined || value === "") return null
  const result = typeof value === "number" ? value : Date.parse(value)
  return Number.isFinite(result) && result > 0 ? result : null
}

export function workoutStartedAt(state) {
  return workoutTimestamp(state.liveSession?.startedAt) || workoutTimestamp(state.workoutStartTime)
}

export function recordedWorkoutSets(state) {
  return Object.values(state.completedSetsMap || {}).reduce((count, sets) => count +
    (Array.isArray(sets) ? sets.filter((set) => Array.isArray(set) && set.length > 0 && !set.skipped && !set.some((item) => item?.skipped)).length : 0), 0)
}

export function workoutElapsedSeconds(state, now = Date.now()) {
  const live = state.liveSession || {}
  if (live.phase === "ending" && Number.isFinite(live.elapsedSeconds)) return Math.max(0, live.elapsedSeconds)
  const start = workoutStartedAt(state)
  return start ? Math.max(0, (now - start) / 1000 - (live.pausedSeconds || 0)) : 0
}

export function isWorkoutStale(state, now = Date.now()) {
  const live = state.liveSession || {}
  const start = workoutStartedAt(state)
  if (!state.activeSplit || !start || live.savedAt) return false
  if (live.phase === "ending") return false
  const watch = state.watchCompanion || {}
  const watchAt = workoutTimestamp(watch.recordedAt)
  if (["running", "active", "paused", "recording"].includes(watch.status) && watchAt && now - watchAt >= 0 && now - watchAt < 90000) return false
  const timer = state.timerState || {}
  if (timer.isActive && Number(timer.startTime) + Number(timer.duration) * 1000 > now) return false
  if (workoutTimestamp(state.liveFeedbackRest?.endsAt) > now) return false
  const recordedActivity = workoutTimestamp(live.lastActivityAt)
  const activity = recordedActivity && recordedActivity >= start && recordedActivity <= now ? recordedActivity : start
  return now - activity >= WORKOUT_REMINDER_IDLE_MS
}

export function workoutReviewTiming(state, now = Date.now()) {
  const live = state.liveSession || {}
  const startedAt = workoutStartedAt(state)
  if (!startedAt || startedAt > now) return { startedAt, endedAt: null, durationSeconds: null }
  if (live.phase === "ending") {
    const duration = live.elapsedSeconds
    const end = workoutTimestamp(live.endedAt) || (Number.isFinite(duration) ? startedAt + (duration + (live.pausedSeconds || 0)) * 1000 : null)
    if (end && end <= now && Number.isFinite(duration) && duration > 0 && duration <= 86400) {
      return { startedAt, endedAt: end, durationSeconds: duration }
    }
    return { startedAt, endedAt: null, durationSeconds: null }
  }
  const activity = workoutTimestamp(live.lastActivityAt)
  if (!activity || activity < startedAt || activity > now) return { startedAt, endedAt: null, durationSeconds: null }
  const durationSeconds = Math.max(0, (activity - startedAt) / 1000 - (live.pausedSeconds || 0))
  return { startedAt, endedAt: activity, durationSeconds: durationSeconds > 0 && durationSeconds <= 86400 ? durationSeconds : null }
}

export function workoutRecoveryInfo(state, userId, now = Date.now(), force = false) {
  const live = state.liveSession || {}
  if (!userId || live.userId !== String(userId) || !state.activeSplit || live.savedAt || (!force && !recordedWorkoutSets(state))) return null
  if (!force && live.phase !== "ending" && !isWorkoutStale(state, now) && !live.pendingSave) return null
  return { ...workoutReviewTiming(state, now), title: state.activeSplit.title || "Training", sets: recordedWorkoutSets(state), clientSessionId: live.clientSessionId }
}

export function isWorkoutExerciseComplete(exercise, rawSets = []) {
  if (!exercise) return false
  const sets = rawSets.filter((set) => Array.isArray(set) && !set.skipped && !set.some((item) => item?.skipped))
  if (exercise.isSuperset) {
    return Boolean(exercise.exercises?.length) && exercise.exercises.every((sub) => {
      const done = sets.filter((set) => !set.isWarmup && String(set.subExerciseId) === String(sub._id))
      return sub.sets?.length > 0 ? done.length >= sub.sets.length : done.length > 0
    })
  }
  if (exercise.title === "Senso Pro") return sets.some((set) => !set.isWarmup)
  if (exercise.sets?.length) return sets.filter((set) => !set.isWarmup).length >= exercise.sets.length
  if (exercise.warmup) return sets.some((set) => set.isWarmup)
  // A free exercise without prescribed sets needs at least one actual entry.
  return sets.some((set) => !set.isWarmup)
}
